import {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useState,
} from "react";
import {useIsLoggedIn} from "../../../../_components/contexts/userInfoContext";
import {Class} from "../../../../../lib/legacyApi/fetchClassesForCourseByCampusId";
import LoadingStatus from "../../../../util/LoadingStatus";
import EnrollStatus from "@/app/types/EnrollStatus";
import getLegacySitePath from "@/app/util/getLegacySitePath";
import {useSearchParams} from "next/navigation";
import {useClearSearchParams} from "@/app/util/useClearSearchParams";
import {StudentForEnrollment} from "@/shared/legacyTypes/fetchEnrollmentDataTypes";
import fetchEnrollmentData from "@/lib/legacyApi/fetchEnrollmentData";

interface EnrollModalInfo {
	students: StudentForEnrollment[];
	isConsultationRequired: boolean;
	isLoggedIn: boolean;
	isLoading: boolean;
	isOpen: boolean;
	isSummer: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	triggerLoad: () => void;
}

/**
 * Hook to get the information needed for the enrollment modal.
 *
 * Loads data lazily when `triggerLoad` is called.
 *
 * @param Class The class to get enrollment information for.
 */
const useEnrollModalInfo = ({
	campusId,
	classId,
	isSummer,
	enrollStatus,
}: Class): EnrollModalInfo => {
	const searchParams = useSearchParams();
	const openClassId = searchParams.get("class");
	const clearSearchParams = useClearSearchParams();

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [students, setStudents] = useState<StudentForEnrollment[]>([]);
	const [loadingState, setLoadingState] = useState<LoadingStatus>(
		LoadingStatus.NOT_LOADED
	);
	const isLoggedIn = useIsLoggedIn();
	const isAnyApproved = students.some((student) => student.is_approved);
	const isConsultationRequired = !isSummer && !isAnyApproved;
	const onLoadingComplete = useCallback(
		(children: StudentForEnrollment[]) => {
			clearSearchParams();
			if (
				enrollStatus !== EnrollStatus.WAITLIST ||
				!children.some((child) => child.is_approved)
			) {
				setIsOpen(true);
				return;
			} else {
				window.open(
					getLegacySitePath(
						`/courses/waitlist-join?class_id=${classId}`,
						campusId
					),
					"_blank"
				);
			}
		},
		[clearSearchParams, enrollStatus, classId, campusId]
	);
	const triggerLoad = useCallback(async () => {
		// If the data is already loaded, don't reload it
		if (loadingState === LoadingStatus.LOADED) {
			onLoadingComplete(students);
			return;
		}

		// TODO: there's probably a way to make this code more concise without
		// making it my arbitrary definition of confusing.  Do that.  Please.
		if (isLoggedIn) {
			setLoadingState(LoadingStatus.LOADING);
			const {children: childrenMap} = await fetchEnrollmentData(classId);
			const children = Object.values(childrenMap);
			setStudents(children);
			setLoadingState(LoadingStatus.LOADED);
			onLoadingComplete(children);
		} else {
			setLoadingState(LoadingStatus.LOADED);
			setIsOpen(true);
		}
	}, [loadingState, isLoggedIn, classId, onLoadingComplete, students]);

	useEffect(() => {
		if (classId === openClassId) {
			triggerLoad();
		}
	}, [classId, openClassId, triggerLoad]);

	return {
		students,
		isConsultationRequired,
		isLoggedIn,
		isLoading: loadingState === LoadingStatus.LOADING,
		isOpen,
		isSummer,
		setIsOpen,
		triggerLoad,
	};
};

export default useEnrollModalInfo;
